import { TicketTrigger } from "@/constants/tickets";
import { groupBy, uniqueId } from "lodash";
import { DataLossAction } from "@/constants/user-data-governance";
import type { DataLossPermissionChoice } from "@/constants/user-data-governance";
import type { PrivacyParametersConfig } from "@/_store/user-data-governance/user-data-governance.module.ts";
import type { UserLabel } from "@/_store/users-settings/user-labels.module";

const ALL_USERS_POLICY = "allUsersPolicy";
const ALL_UNPROTECTED_INTERNAL_USERS = "allUnprotectedInternalUsersPolicy";

const categoryOrder = [
  TicketTrigger.DLP_PERSONAL,
  TicketTrigger.DLP_HEALTH,
  TicketTrigger.DLP_CREDIT_CARD,
  TicketTrigger.DLP_NON_PUBLIC,
  TicketTrigger.CRITICAL_DATA_PASSWORD,
  TicketTrigger.CRITICAL_DATA_CERTIFICATE,
  TicketTrigger.CRITICAL_DATA_SOURCE_CODE,
  TicketTrigger.CRITICAL_DATA_KEYWORDS,
  TicketTrigger.CRITICAL_DATA_FILE_TYPES,
];

export enum PermissionsHolderType {
  USER_DOMAIN = "USER_DOMAIN",
  GROUP = "GROUP",
  ALL_USERS = "ALL_USERS",
  ALL_UNPROTECTED_INTERNAL_USERS = "ALL_UNPROTECTED_INTERNAL_USERS",
  LABEL = "LABEL",
}

interface PermissionsGroupPolicy {
  groupId: string;
  name: string;
  grantType: DataLossPermissionChoice;
  serviceName: string;
}

interface PermissionsLabelPolicy {
  id: string;
  name: string;
  color: string;
  grantType: DataLossPermissionChoice;
}

export interface UserDataGovernancePermissionConfig {
  groupPolicies: PermissionsGroupPolicy[];
  labelPolicies?: PermissionsLabelPolicy[];
  emailDomainPolicies: Record<string, DataLossPermissionChoice>;
  allUsersPolicy: DataLossPermissionChoice;
  allUnprotectedInternalUsersPolicy: DataLossPermissionChoice;
  emailAliases: Record<string, DataLossPermissionChoice>;
}

export type DataLossPermissionsRaw = {
  [key in TicketTrigger]?: UserDataGovernancePermissionConfig;
};

export interface DataLossPermissionsNormalized {
  id: string;
  name: string;
  serviceName?: string;
  groupId?: string;
  color?: string;
  choice: DataLossPermissionChoice;
  position: number;
  category: TicketTrigger;
  aliases?: string[];
}

export interface PermissionsFormData {
  users: Array<string | { email: string }>;
  groups: PermissionsGroupPolicy[];
  labels: UserLabel[];
  action: DataLossAction;
  choice: DataLossPermissionChoice;
  selectedDataType: TicketTrigger;
}

export interface PermissionsDataForCreation {
  holderType: PermissionsHolderType;
  data: {
    triggers: TicketTrigger[];
    grantType: DataLossPermissionChoice;
    holders?: string[];
  };
}

export const mapDataLossPermissionCategory = ([category, data]: [
  TicketTrigger,
  UserDataGovernancePermissionConfig,
]): DataLossPermissionsNormalized[] => {
  const position = categoryOrder.indexOf(category);
  const allUsersPolicy = data[ALL_USERS_POLICY]
    ? [
        {
          id: uniqueId("permission_"),
          name: ALL_USERS_POLICY,
          choice: data[ALL_USERS_POLICY],
          position,
          category,
        },
      ]
    : [];
  const groups = data.groupPolicies.map(({ groupId, name, grantType, serviceName }) => {
    return {
      id: uniqueId("permission_"),
      groupId,
      name,
      choice: grantType,
      position,
      category,
      serviceName,
    };
  });
  const labels = (data.labelPolicies || []).map(({ id, name, color, grantType }) => {
    return {
      id,
      name,
      color,
      choice: grantType,
      position,
      category,
    };
  });
  const users = Object.entries(data.emailDomainPolicies).map(([name, choice]) => {
    const aliases = data?.emailAliases?.[name] || [];
    return {
      id: uniqueId("permission_"),
      name,
      choice,
      position,
      category,
      aliases,
    };
  });

  const allUnprotectedInternalUsersPolicy = data[ALL_UNPROTECTED_INTERNAL_USERS]
    ? [
        {
          id: uniqueId("permission_"),
          name: ALL_UNPROTECTED_INTERNAL_USERS,
          choice: data[ALL_UNPROTECTED_INTERNAL_USERS],
          position,
          category,
        },
      ]
    : [];

  return [...allUsersPolicy, ...allUnprotectedInternalUsersPolicy, ...groups, ...labels, ...users];
};

export const denormalizePermissionsData = (
  tableData: DataLossPermissionsNormalized[]
): DataLossPermissionsRaw => {
  const grouped = groupBy(tableData, "category");

  return Object.fromEntries(
    Object.entries(grouped).map(([category, value]) => {
      const allUsersPolicy = value.find((v) => v.name === ALL_USERS_POLICY)?.choice;
      const allUnprotectedInternalUsersPolicy = value.find(
        (v) => v.name === ALL_UNPROTECTED_INTERNAL_USERS
      )?.choice;
      const groupPolicies = value
        .filter((v) => v.groupId)
        .map(({ groupId, name, choice: grantType }) => ({
          groupId,
          name,
          grantType,
        }));
      const labelPolicies = value
        .filter((v) => v.color)
        .map(({ id, name, color, choice: grantType }) => ({
          id,
          name,
          color: color!,
          grantType,
        }));
      const emailDomainPolicies = value
        .filter(
          (v) =>
            !v.groupId &&
            !v.color &&
            ![ALL_USERS_POLICY, ALL_UNPROTECTED_INTERNAL_USERS].includes(v.name)
        )
        .reduce(
          (acc, curr) => {
            acc[curr.name] = curr.choice;
            return acc;
          },
          {} as Record<string, DataLossPermissionChoice>
        );

      return [
        category,
        {
          allUsersPolicy,
          allUnprotectedInternalUsersPolicy,
          groupPolicies,
          labelPolicies,
          emailDomainPolicies,
        },
      ];
    })
  );
};

export const denormalizePermissionsForCreation = (
  payload: PermissionsFormData
): PermissionsDataForCreation => {
  const actionParamMap = {
    [DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS]: PermissionsHolderType.USER_DOMAIN,
    [DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_GROUPS]: PermissionsHolderType.GROUP,
    [DataLossAction.ADD_PERMISSION_FOR_ALL_USERS]: PermissionsHolderType.ALL_USERS,
    [DataLossAction.ADD_UNPROTECTED_INTERNAL_USERS]:
      PermissionsHolderType.ALL_UNPROTECTED_INTERNAL_USERS,
    [DataLossAction.ADD_PERMISSION_FOR_LABELS]: PermissionsHolderType.LABEL,
  };
  let holders;
  if (payload.action === DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_GROUPS) {
    holders = payload.groups.map(({ groupId }) => groupId);
  }
  if (payload.action === DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS) {
    holders = payload.users.map((user) => (typeof user === "string" ? user : user.email));
  }
  if (payload.action === DataLossAction.ADD_PERMISSION_FOR_LABELS) {
    holders = payload.labels.map(({ id }) => id);
  }
  return {
    holderType: actionParamMap[payload.action],
    data: {
      triggers: [payload.selectedDataType],
      grantType: payload.choice,
      holders,
    },
  };
};

export const getRecommendedAndOtherDataTypes = (data: PrivacyParametersConfig) => {
  const recommendedDataTypes: string[] = [];
  const otherDataTypes: string[] = [];
  const ignoredKeys = ["wizardParameters", "valid"];
  Object.keys(data).forEach((key) => {
    if (ignoredKeys.includes(key as string)) return;
    if (data[key as keyof PrivacyParametersConfig]) {
      recommendedDataTypes.push(key);
    } else {
      otherDataTypes.push(key);
    }
  });
  return {
    recommendedDataTypes,
    otherDataTypes,
  };
};
